import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useMultiStepForm } from '../../../ui-components/base/MultiStepForm';
import Input from '../../../ui-components/base/Input';
import Textarea from '../../../ui-components/base/Textarea';
import SelectDropdown from '../../../ui-components/base/SelectDropdown';
import RadioButtonGroup from '../../../ui-components/base/RadioButtonGroup';
import Button from '../../../ui-components/base/Button';
import Skeleton from '../../../ui-components/base/Skeleton';
import { setNotificationData } from '../../../redux/actions/uiActions';
import styles from './BannerInformationStep.module.scss';
import DesktopIcon from '../../../components/icons/DesktopIcon';
import MobileIcon from '../../../components/icons/MobileIcon';
import imageIcon from '../../../styles/images/image_icon.svg';

const IMAGE_DESCRIPTION_MAX_LENGTH = 100;

const BannerInformationStep = ({ isLoading = false }) => {
  const { stepData, updateStepData, setStepValid } = useMultiStepForm();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    bannerType: stepData.bannerType || 'button',
    title: stepData.title || '',
    description: stepData.description || '',
    link: stepData.link || '', // For Image Link mode
    imageDescription: stepData.imageDescription || '',
    primaryButtonText: stepData.primaryButtonText || '',
    primaryButtonLink: stepData.primaryButtonLink || '',
    secondaryButtonText: stepData.secondaryButtonText || '',
    secondaryButtonLink: stepData.secondaryButtonLink || '',
    buttonColor: stepData.buttonColor || 'white',
    desktopBannerImage: stepData.desktopBannerImage || null,
    mobileBannerImage: stepData.mobileBannerImage || null,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [activeTab, setActiveTab] = useState('desktop'); // Tab state management
  const [desktopImagePreview, setDesktopImagePreview] = useState(null);
  const [mobileImagePreview, setMobileImagePreview] = useState(null);

  // Use refs to track previous preview URLs for cleanup
  const prevDesktopPreviewRef = useRef(null);
  const prevMobilePreviewRef = useRef(null);

  // Tracks whether we've already hydrated formData from stepData once, so this
  // effect can't re-fire and clobber a field the user just cleared.
  const hasHydratedRef = useRef(false);

  // Sync form data when stepData changes (for edit mode hydration)
  useEffect(() => {
    if (hasHydratedRef.current) {
      return;
    }

    // Check if stepData has meaningful data (title or images)
    const stepDataHasContent = stepData && (stepData.title || stepData.desktopBannerImage);

    // Initialize form when:
    // 1. Not currently loading
    // 2. stepData has content
    // 3. We haven't hydrated yet
    if (!isLoading && stepDataHasContent) {
      setFormData({
        bannerType: stepData.bannerType || 'button',
        title: stepData.title || '',
        description: stepData.description || '',
        link: stepData.link || '',
        imageDescription: stepData.imageDescription || '',
        primaryButtonText: stepData.primaryButtonText || '',
        primaryButtonLink: stepData.primaryButtonLink || '',
        secondaryButtonText: stepData.secondaryButtonText || '',
        secondaryButtonLink: stepData.secondaryButtonLink || '',
        buttonColor: stepData.buttonColor || 'white',
        desktopBannerImage: stepData.desktopBannerImage || null,
        mobileBannerImage: stepData.mobileBannerImage || null,
      });
      hasHydratedRef.current = true;
    }
  }, [stepData, isLoading]);

  // Initialize preview URLs from formData
  useEffect(() => {
    if (formData.desktopBannerImage && !desktopImagePreview) {
      // Check if it's a File object or an existing image reference
      if (formData.desktopBannerImage instanceof File) {
        const previewUrl = URL.createObjectURL(formData.desktopBannerImage);
        setDesktopImagePreview(previewUrl);
      } else if (formData.desktopBannerImage.href) {
        // It's an existing image from the API
        setDesktopImagePreview(formData.desktopBannerImage.href);
      }
    }
    if (formData.mobileBannerImage && !mobileImagePreview) {
      if (formData.mobileBannerImage instanceof File) {
        const previewUrl = URL.createObjectURL(formData.mobileBannerImage);
        setMobileImagePreview(previewUrl);
      } else if (formData.mobileBannerImage.href) {
        setMobileImagePreview(formData.mobileBannerImage.href);
      }
    }
  }, [formData.desktopBannerImage, formData.mobileBannerImage, desktopImagePreview, mobileImagePreview]);

  // Determine if we're in Image Link mode
  const isImageLinkMode = formData.bannerType === 'image';

  // Image resolution requirements
  const IMAGE_REQUIREMENTS = {
    desktop: { width: 1440, height: 324 },
    mobile: { width: 375, height: 264 }
  };

  // URL validation helper - accepts absolute HTTP(S) URLs as well as relative URLs
  // (e.g. "/abc/xyz" or "/abc?id=xyz") that start with a leading slash.
  const isValidURL = (url) => {
    if (!url || !url.trim()) return true; // Empty is valid (required check is separate)
    const trimmedUrl = url.trim();

    // Relative URL - must start with a leading slash
    if (trimmedUrl.startsWith('/')) return true;

    try {
      const urlObj = new URL(trimmedUrl);
      // Ensure it's HTTP or HTTPS protocol
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch (error) {
      return false;
    }
  };

  // Validate form based on banner type
  useEffect(() => {
    const newErrors = {};

    // Title is required for Button Link only; optional for Image Link
    if (!isImageLinkMode && (!formData.title || formData.title.trim().length === 0)) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 45) {
      newErrors.title = 'Title must be 45 characters or less';
    }

    if (isImageLinkMode) {
      // Image Link Mode Validation
      // Link is required
      if (!formData.link || formData.link.trim().length === 0) {
        newErrors.link = 'Link is required';
      } else if (!isValidURL(formData.link)) {
        newErrors.link = 'Must be a valid HTTP(S) URL (e.g., https://example.com) or a relative URL starting with / (e.g., /abc/xyz)';
      }
    } else {
      // Button Link Mode Validation
      // Primary button text is required
      if (!formData.primaryButtonText || formData.primaryButtonText.trim().length === 0) {
        newErrors.primaryButtonText = 'Primary button text is required';
      } else if (formData.primaryButtonText.length > 15) {
        newErrors.primaryButtonText = 'Must be 15 characters or less';
      }

      // Primary button link is required
      if (!formData.primaryButtonLink || formData.primaryButtonLink.trim().length === 0) {
        newErrors.primaryButtonLink = 'Primary button link is required';
      } else if (!isValidURL(formData.primaryButtonLink)) {
        newErrors.primaryButtonLink = 'Must be a valid HTTP(S) URL (e.g., https://example.com) or a relative URL starting with / (e.g., /abc/xyz)';
      }

      // Secondary button text validation (if provided)
      if (formData.secondaryButtonText && formData.secondaryButtonText.length > 15) {
        newErrors.secondaryButtonText = 'Must be 15 characters or less';
      }

      // Secondary button link validation (if provided)
      if (formData.secondaryButtonLink && formData.secondaryButtonLink.trim() && !isValidURL(formData.secondaryButtonLink)) {
        newErrors.secondaryButtonLink = 'Must be a valid HTTP(S) URL (e.g., https://example.com) or a relative URL starting with / (e.g., /abc/xyz)';
      }
    }

    // Image description is required for both modes
    if (!formData.imageDescription || formData.imageDescription.trim().length === 0) {
      newErrors.imageDescription = 'Image description is required';
    } else if (formData.imageDescription.length > IMAGE_DESCRIPTION_MAX_LENGTH) {
      newErrors.imageDescription = `Image description must be ${IMAGE_DESCRIPTION_MAX_LENGTH} characters or less`;
    }

    // Button and text color is required for both modes
    if (formData.buttonColor !== 'white' && formData.buttonColor !== 'black') {
      newErrors.buttonColor = 'Button and text color is required';
    }

    // Banner images are required for both modes
    if (!formData.desktopBannerImage) {
      newErrors.desktopBannerImage = 'Desktop banner image is required';
    }
    if (!formData.mobileBannerImage) {
      newErrors.mobileBannerImage = 'Mobile banner image is required';
    }

    setErrors(newErrors);

    // Update validation status - step is valid when no errors exist
    const isValid = Object.keys(newErrors).length === 0;
    setStepValid(isValid);

    // Always update step data (even if not valid) so other steps can access it
    updateStepData(formData);
  }, [formData, isImageLinkMode]);

  const handleChange = (field) => (value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // When switching banner type, preserve all field values
      // Validation and payload sanitization will handle which fields are relevant
      if (field === 'bannerType') {
        // Reset touched state for fields that are now hidden to clear validation errors
        setTouched((prevTouched) => {
          const newTouched = { ...prevTouched };
          if (value === 'image') {
            // Switching to Image Link mode - clear touched state for button fields
            delete newTouched.primaryButtonText;
            delete newTouched.primaryButtonLink;
            delete newTouched.secondaryButtonText;
            delete newTouched.secondaryButtonLink;
          } else {
            // Switching to Button Link mode - clear touched state for link field
            delete newTouched.link;
          }
          return newTouched;
        });
      }

      return updated;
    });
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Validate image dimensions
  const validateImageDimensions = (file, deviceType) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        const { width, height } = img;
        const required = IMAGE_REQUIREMENTS[deviceType];

        URL.revokeObjectURL(url);

        if (width === required.width && height === required.height) {
          resolve(true);
        } else {
          reject({
            actual: { width, height },
            required
          });
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject({ error: 'Failed to load image' });
      };

      img.src = url;
    });
  };

  const handleImageUpload = async (event, deviceType) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      dispatch(setNotificationData({
        show: true,
        type: 'FAILURE',
        title: 'Invalid File Type',
        description: 'Please upload a .jpg, .jpeg or .webp file',
      }));
      event.target.value = ''; // Reset input
      return;
    }

    // Validate file size (3MB max)
    const maxSize = 3 * 1024 * 1024; // 3MB in bytes
    if (file.size > maxSize) {
      dispatch(setNotificationData({
        show: true,
        type: 'FAILURE',
        title: 'File Too Large',
        description: 'File size must be less than 3MB',
      }));
      event.target.value = ''; // Reset input
      return;
    }

    // Validate image dimensions
    try {
      await validateImageDimensions(file, deviceType);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);

      if (deviceType === 'desktop') {
        setFormData((prev) => ({ ...prev, desktopBannerImage: file }));
        setDesktopImagePreview(previewUrl);
        setTouched((prev) => ({ ...prev, desktopBannerImage: true }));
      } else {
        setFormData((prev) => ({ ...prev, mobileBannerImage: file }));
        setMobileImagePreview(previewUrl);
        setTouched((prev) => ({ ...prev, mobileBannerImage: true }));
      }

      dispatch(setNotificationData({
        show: true,
        type: 'SUCCESS',
        title: 'Image Uploaded Successfully',
        description: `${deviceType === 'desktop' ? 'Desktop' : 'Mobile'} banner image uploaded`,
      }));
    } catch (error) {
      if (error.actual) {
        const { actual, required } = error;
        dispatch(setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Invalid Image Resolution',
          description: `${deviceType === 'desktop' ? 'Desktop' : 'Mobile'} banner must be ${required.width}px × ${required.height}px. Your image is ${actual.width}px × ${actual.height}px.`,
        }));
      } else {
        dispatch(setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Image Upload Failed',
          description: error.error || 'Failed to process image',
        }));
      }
      event.target.value = ''; // Reset input
    }
  };

  const handleUploadClick = (deviceType) => {
    document.getElementById(`${deviceType}-banner-image-input`).click();
  };

  const handleRemoveImage = (deviceType) => {
    if (deviceType === 'desktop') {
      // Only revoke if it's a blob URL (not an external URL)
      if (desktopImagePreview && desktopImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(desktopImagePreview);
      }
      setFormData((prev) => ({ ...prev, desktopBannerImage: null }));
      setDesktopImagePreview(null);
      setTouched((prev) => ({ ...prev, desktopBannerImage: false }));
    } else {
      if (mobileImagePreview && mobileImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(mobileImagePreview);
      }
      setFormData((prev) => ({ ...prev, mobileBannerImage: null }));
      setMobileImagePreview(null);
      setTouched((prev) => ({ ...prev, mobileBannerImage: false }));
    }
  };

  // Cleanup old desktop preview URL when it changes
  useEffect(() => {
    const prevUrl = prevDesktopPreviewRef.current;

    if (prevUrl && prevUrl !== desktopImagePreview && prevUrl.startsWith('blob:')) {
      URL.revokeObjectURL(prevUrl);
    }

    prevDesktopPreviewRef.current = desktopImagePreview;
  }, [desktopImagePreview]);

  // Cleanup old mobile preview URL when it changes
  useEffect(() => {
    const prevUrl = prevMobilePreviewRef.current;

    if (prevUrl && prevUrl !== mobileImagePreview && prevUrl.startsWith('blob:')) {
      URL.revokeObjectURL(prevUrl);
    }

    prevMobilePreviewRef.current = mobileImagePreview;
  }, [mobileImagePreview]);

  // Cleanup all preview URLs on unmount
  useEffect(() => {
    return () => {
      if (desktopImagePreview && desktopImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(desktopImagePreview);
      }
      if (mobileImagePreview && mobileImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(mobileImagePreview);
      }
    };
  }, []); // Empty deps = only runs on unmount

  return (
    <div className={styles.bannerInfoStep}>
      <div className={styles.formLayout}>
        {/* Left Column - Banner Configuration */}
        <div className={styles.leftColumn}>
          <RadioButtonGroup
            label="Banner Type"
            name="bannerType"
            options={[
              { value: 'button', label: 'Button Link' },
              { value: 'image', label: 'Image Link' },
            ]}
            value={formData.bannerType}
            onChange={handleChange('bannerType')}
            showInfoIcon
            infoText={
              isImageLinkMode
                ? 'Use when you want the entire banner image to serve as a clickable link.'
                : 'Use when you want to display a call-to-action button (e.g., Learn More, Register Now) on the banner.'
            }
            required
            isLoading={isLoading}
          />

          <Input
            label="Title"
            placeholder="Enter Banner Title"
            value={formData.title}
            onChange={handleChange('title')}
            name="title"
            maxLength={45}
            charLimit={45}
            required={!isImageLinkMode}
            error={touched.title && !!errors.title}
            errorMessage={touched.title ? errors.title : ''}
            isLoading={isLoading}
          />

          <Textarea
            label="Description"
            placeholder="Enter Banner Description"
            value={formData.description}
            onChange={handleChange('description')}
            name="description"
            maxLength={150}
            charLimit={150}
            rows={3}
            // showDragHandle
            isLoading={isLoading}
          />

          {/* Image Link Mode - Show only Link field */}
          {isImageLinkMode && (
            <Input
              label="Link"
              placeholder="https:// or /example?id=123"
              type="url"
              value={formData.link}
              onChange={handleChange('link')}
              name="link"
              required
              error={touched.link && !!errors.link}
              errorMessage={touched.link ? errors.link : ''}
              isLoading={isLoading}
            />
          )}

          {/* Button Link Mode - Show button-specific fields */}
          {!isImageLinkMode && (
            <>
              <div className={styles.formRow}>
                <Input
                  label="Primary Button Text"
                  placeholder="e.g., Learn more"
                  value={formData.primaryButtonText}
                  onChange={handleChange('primaryButtonText')}
                  name="primaryButtonText"
                  maxLength={15}
                  charLimit="Max 15 chars"
                  required
                  error={touched.primaryButtonText && !!errors.primaryButtonText}
                  errorMessage={touched.primaryButtonText ? errors.primaryButtonText : ''}
                  isLoading={isLoading}
                />

                <Input
                  label="Primary Button Link"
                  placeholder="https:// or /example?id=123"
                  type="url"
                  value={formData.primaryButtonLink}
                  onChange={handleChange('primaryButtonLink')}
                  name="primaryButtonLink"
                  required
                  error={touched.primaryButtonLink && !!errors.primaryButtonLink}
                  errorMessage={touched.primaryButtonLink ? errors.primaryButtonLink : ''}
                  isLoading={isLoading}
                />
              </div>

              <div className={styles.formRow}>
                <Input
                  label="Secondary Button Text"
                  placeholder="e.g., Learn more"
                  value={formData.secondaryButtonText}
                  onChange={handleChange('secondaryButtonText')}
                  name="secondaryButtonText"
                  maxLength={15}
                  charLimit="Max 15 chars"
                  error={touched.secondaryButtonText && !!errors.secondaryButtonText}
                  errorMessage={touched.secondaryButtonText ? errors.secondaryButtonText : ''}
                  isLoading={isLoading}
                />

                <Input
                  label="Secondary Button Link"
                  placeholder="https:// or /example?id=123"
                  type="url"
                  value={formData.secondaryButtonLink}
                  onChange={handleChange('secondaryButtonLink')}
                  name="secondaryButtonLink"
                  error={touched.secondaryButtonLink && !!errors.secondaryButtonLink}
                  errorMessage={touched.secondaryButtonLink ? errors.secondaryButtonLink : ''}
                  isLoading={isLoading}
                />
              </div>
            </>
          )}

          {/* Button and Text Color - shown for both modes */}
          <div className={styles.colorDropdown}>
            <SelectDropdown
              label="Button and Text Color"
              options={[
                { value: 'white', label: 'White' },
                { value: 'black', label: 'Black' },
              ]}
              value={formData.buttonColor}
              onChange={handleChange('buttonColor')}
              name="buttonColor"
              required
              error={touched.buttonColor && !!errors.buttonColor}
              errorMessage={touched.buttonColor ? errors.buttonColor : ''}
              isLoading={isLoading}
            />
          </div>
        </div>

        {/* Right Column - Banner Image Upload */}
        <div className={styles.rightColumn}>
          <div className={styles.imageSection}>
            <div className={styles.imageHeader}>
              <p className={styles.imageTitle}>
                Banner Image<span className={styles.required}>*</span>
              </p>
              <p className={styles.imageDescription}>
                Add a Banner Image by choosing an Image from your local device.
              </p>
            </div>

            {/* Tab Controls */}
            <div className={styles.deviceViewControls}>
              <div className={styles.deviceTabs}>
                <button
                  type="button"
                  className={`${styles.deviceTab} ${activeTab === 'desktop' ? styles.active : ''}`}
                  onClick={() => setActiveTab('desktop')}
                >
                  <DesktopIcon color={activeTab === 'desktop' ? 'white': '#2c2c2c'}/>
                  <span className={styles.tabLabel}>Desktop/Tablet</span>
                </button>
                <button
                  type="button"
                  className={`${styles.deviceTab} ${activeTab === 'mobile' ? styles.active : ''}`}
                  onClick={() => setActiveTab('mobile')}
                >
                  <MobileIcon color={activeTab === 'mobile' ? 'white': '#2C2C2C'}/>
                  <span className={styles.tabLabel}>Mobile</span>
                </button>
              </div>
              <p className={styles.resolutionText}>
                {activeTab === 'desktop'
                  ? 'Desktop | 1440px X 324px'
                  : 'Mobile | 375px X 264px'}
              </p>
            </div>

            {/* Desktop Tab Content */}
            {activeTab === 'desktop' && (
              <div className={styles.uploadBlock}>
                {!isLoading && desktopImagePreview && (
                  <button
                    type="button"
                    className={styles.uploadNewButton}
                    onClick={() => handleUploadClick('desktop')}
                  >
                    Upload New Image
                  </button>
                )}

                <div className={styles.uploadContainer}>
                  {!isLoading && (
                    <input
                      type="file"
                      id="desktop-banner-image-input"
                      className={styles.fileInput}
                      accept=".jpg,.jpeg,.webp"
                      onChange={(e) => handleImageUpload(e, 'desktop')}
                    />
                  )}

                  {isLoading ? (
                    <Skeleton height="200px" borderRadius="8px" />
                  ) : !desktopImagePreview ? (
                    <div className={styles.dropzone} onClick={() => handleUploadClick('desktop')}>
                      <div className={styles.uploadIcon}>
                        <i className="fa fa-cloud-upload-alt" />
                        <img src={imageIcon} alt="Upload Image Icon" />
                      </div>
                      <button
                        type="button"
                        className={styles.uploadButton}
                      >
                        Choose Image from Local Computer
                      </button>
                    </div>
                  ) : (
                    <div className={styles.imagePreview}>
                      <img src={desktopImagePreview} alt="Desktop banner preview" />
                    </div>
                  )}

                  {!isLoading && touched.desktopBannerImage && errors.desktopBannerImage && !formData.desktopBannerImage && (
                    <p className={styles.error}>{errors.desktopBannerImage}</p>
                  )}

                  <p className={styles.uploadSpecs}>3MB max | .jpg, .jpeg, .webp</p>
                </div>
              </div>
            )}

            {/* Mobile Tab Content */}
            {activeTab === 'mobile' && (
              <div className={styles.uploadBlock}>
                {!isLoading && mobileImagePreview && (
                  <button
                    type="button"
                    className={styles.uploadNewButton}
                    onClick={() => handleUploadClick('mobile')}
                  >
                    Upload New Image
                  </button>
                )}

                <div className={styles.uploadContainer}>
                  {!isLoading && (
                    <input
                      type="file"
                      id="mobile-banner-image-input"
                      className={styles.fileInput}
                      accept=".jpg,.jpeg,.webp"
                      onChange={(e) => handleImageUpload(e, 'mobile')}
                    />
                  )}

                  {isLoading ? (
                    <Skeleton height="200px" borderRadius="8px" />
                  ) : !mobileImagePreview ? (
                    <div className={styles.dropzone} onClick={() => handleUploadClick('mobile')}>
                      <div className={styles.uploadIcon}>
                        <i className="fa fa-cloud-upload-alt" />
                        <img src={imageIcon} alt="Upload Image Icon" />
                      </div>
                      <button
                        type="button"
                        className={styles.uploadButton}
                      >
                        Choose Image from Local Computer
                      </button>
                    </div>
                  ) : (
                    <div className={styles.imagePreview}>
                      <img src={mobileImagePreview} alt="Mobile banner preview" />
                    </div>
                  )}

                  {!isLoading && touched.mobileBannerImage && errors.mobileBannerImage && !formData.mobileBannerImage && (
                    <p className={styles.error}>{errors.mobileBannerImage}</p>
                  )}

                  <p className={styles.uploadSpecs}>3MB max | .jpg, .jpeg, .webp</p>
                </div>
              </div>
            )}
          </div>

          {/* Image Description - shown for both modes */}
          <Textarea
            label="Image Description/Alt Tag"
            placeholder="Enter Image Description"
            value={formData.imageDescription}
            onChange={handleChange('imageDescription')}
            name="imageDescription"
            maxLength={IMAGE_DESCRIPTION_MAX_LENGTH}
            charLimit={`${formData.imageDescription.length}/${IMAGE_DESCRIPTION_MAX_LENGTH}`}
            rows={3}
            // showDragHandle
            required
            error={touched.imageDescription && !!errors.imageDescription}
            errorMessage={touched.imageDescription ? errors.imageDescription : ''}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

BannerInformationStep.propTypes = {
  isLoading: PropTypes.bool,
};

export default BannerInformationStep;
